import { fetchSignedDocumentFile, openSignedDocumentPdf } from '../signing/signing.api';
import {
  DOCUMENT_SOURCE,
  DOCUMENT_STATE,
  DOCUMENTS_QUERY_KEY,
  type DocumentListItem,
  fetchDocumentFile,
  openDocumentPdf,
} from './documents.api';

/** Cât are nevoie deschiderea ca să aleagă drumul — nu tot rândul. */
export type OpenableDocument = Pick<DocumentListItem, 'id' | 'documentState' | 'source'>;

/**
 * **Care rută întoarce conținutul unui document.** Trei cazuri, un singur loc.
 *
 * | documentul | de unde vine conținutul |
 * |---|---|
 * | primit (#109) | `GET /documents/:id/pdf` — fișierul venit pe email |
 * | generat, semnat **sau reziliat** | ruta de semnare — **fișierul salvat la semnare**, cu semnătura pe el |
 * | generat, restul stărilor | `GET /documents/:id/pdf` — randarea versiunii înghețate |
 *
 * ⚠️ **Un act primit merge pe ruta documentului și când e în starea `semnat`.** N-a trecut
 * prin sesiunea noastră de semnare, deci n-are fișier acolo, iar ruta de semnare i-ar fi
 * răspuns „nu există". De când registrul ține și acte primite, „semnat" nu mai înseamnă
 * „semnat la noi".
 *
 * ⚠️ **`reziliat` intră aici, și e chiar miezul lui
 * [#140](https://github.com/bplugaru/efx-pcrm/issues/140).** Un act reziliat **a fost**
 * semnat — e singurul drum către starea asta — deci fișierul semnat există și rămâne dovada
 * perioadei în care contractul a produs efecte. Lăsat pe randare, „Vezi document" ar fi
 * tipărit contractul semnat ca nesemnat: exact defectul reparat pe 2026-09-01, întors pe altă
 * stare.
 */
function dinSemnare(document: OpenableDocument): boolean {
  return (
    document.source === DOCUMENT_SOURCE.generated &&
    (document.documentState === DOCUMENT_STATE.signed ||
      document.documentState === DOCUMENT_STATE.terminated)
  );
}

/**
 * Conținutul, **într-o filă nouă**. Folosit de „Vezi PDF" / „Vezi document" din meniul de
 * acțiuni.
 */
export function openDocumentFile(document: OpenableDocument): Promise<void> {
  return dinSemnare(document) ? openSignedDocumentPdf(document.id) : openDocumentPdf(document.id);
}

/**
 * Același conținut, **ca blob**: pagina documentului îl arată pe loc, într-un cadru.
 *
 * Aceeași alegere de rută ca la deschiderea în filă nouă, scrisă o singură dată: o a doua
 * copie ar fi divergat la prima stare nouă — și chiar a divergat o dată, pagina rămânând pe
 * randare după ce meniul trecuse pe ruta de semnare.
 */
export function fetchDocumentContent(document: OpenableDocument): Promise<Blob> {
  return dinSemnare(document)
    ? fetchSignedDocumentFile(document.id)
    : fetchDocumentFile(document.id);
}

/**
 * Cheia sub care stă blobul în cache.
 *
 * ⚠️ **Ruta intră în cheie.** Cele două rute întorc fișiere diferite pentru același `id` —
 * randarea fără semnătură și actul semnat — iar o cheie comună le-ar fi amestecat la prima
 * semnare petrecută cu pagina deschisă.
 */
export function documentFileQueryKey(document: OpenableDocument): readonly unknown[] {
  return [
    ...DOCUMENTS_QUERY_KEY,
    document.id,
    'file',
    dinSemnare(document) ? 'signing' : 'document',
  ] as const;
}

/**
 * **Pagina documentului arată fișierul, nu editorul?**
 *
 * Două cazuri, din motive diferite: actul **primit** n-a fost niciodată text, iar actul
 * **semnat** trebuie văzut cu semnătura pe el — editorul ar fi desenat versiunea înghețată,
 * adică exact contractul semnat, tipărit ca nesemnat.
 *
 * ⚠️ **Actul anulat nu intră aici.** N-are fișier semnat, deci n-ar avea ce arăta în plus
 * față de editorul stins — iar acolo se citește motivul anulării. **Cel reziliat intră**: el
 * a fost semnat, iar fișierul lui e chiar ce trebuie văzut (#140).
 */
export function showsFile(document: OpenableDocument): boolean {
  return document.source === DOCUMENT_SOURCE.received || dinSemnare(document);
}
